import { AnnotatePanelProps } from "../annotate";
import { Combobox } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { PlusIcon } from "@heroicons/react/24/outline";
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import type { SaveArticleTagsMutation, SaveArticleTagsMutationVariables } from "~/graphql/codegen/graphql";
import { SAVE_ARTICLE_TAGS_MUTATION } from "../annotate-mutations";
import { useAutoSave } from "~/hooks/autosave";
import { classNames } from "~/utils";
import metrics from "~/utils/metrics";
import { toast, Badge } from "@mindstonehq/ui";
import { useTags } from "~/hooks/context-hook";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function TagsPanel({
  article,
  close,
  selectedHighlight,
  setSelectedHighlight,
  refresh,
}: AnnotatePanelProps) {
  const { t } = useLingui();
  const [textFieldInput, setTextFieldInput] = useState<string>("");
  const [tagsValue, setTagsValue] = React.useState<string[]>(article.tags || []);
  const inputRef = useRef<HTMLInputElement>(null);
  const userTags = useTags();
  const [saveArticleTagsMutation] = useMutation<SaveArticleTagsMutation, SaveArticleTagsMutationVariables>(
    SAVE_ARTICLE_TAGS_MUTATION,
  );
  const [unusedUserTags, setUnusedUserTags] = useState<string[]>([]);

  function saveTags(tags: string[]) {
    metrics.track("tags_update", {
      article_id: article.id,
      view: "annotate",
    });
    return saveArticleTagsMutation({
      variables: { articleId: article.id, tags: tags },
    })
      .then((res) => {
        refresh();
      })
      .catch((error) => {
        toast.error(t(msg`We couldn't remove the tags. Please try again later.`));
        throw error; // Propagate
      });
  }

  const [savedTags, setSaveTags, saved, saving, errorMsg] = useAutoSave<string[]>(article.tags, saveTags);

  useEffect(() => {
    setTagsValue(article.tags);
  }, [article.tags]);

  useEffect(() => {
    // update at start
    updateUnusedTags();
  }, [userTags.length]);

  useEffect(() => {
    //update after refresh
    if (!userTags || !article.tags) return;
    updateUnusedTags();
  }, [userTags.length, article.tags.length]);

  useEffect(() => {
    if (tagsValue.join(",") !== article.tags.join(",")) {
      setSaveTags(tagsValue);
    }
  }, [tagsValue]);

  const updateUnusedTags = () => {
    setUnusedUserTags(() => {
      return userTags.filter((x) => {
        return !tagsValue.includes(x);
      });
    });
  };

  const lowercaseTextfield = textFieldInput.toLowerCase();

  const existingTags = [
    ...tagsValue.filter((x) => {
      if (!textFieldInput) {
        return false;
      }
      return x.includes(lowercaseTextfield);
    }),
    ...userTags.filter((x) => {
      if (!textFieldInput) {
        return false;
      }
      return x.includes(lowercaseTextfield);
    }),
  ];

  const filteredTags = new Set<string>([...existingTags.splice(0, Math.min(10, existingTags.length)), textFieldInput]);

  return (
    <div className="h-full overflow-y-auto flex flex-col">
      <div className="flex justify-between items-center py-5 px-6 bg-background">
        <div className="text-lg font-bold"><Trans>Tags ({article.tags.length})</Trans></div>
      </div>
      <hr />
      <div className="p-6 grow overflow-y-auto">
        {/* INPUT */}
        <Combobox
          multiple
          value={tagsValue}
          onChange={(v) => {
            if (inputRef.current) {
              // For some reason, headlessUI doens't clear in the input once a value is selected. So forcing it
              inputRef.current.value = "";
            }

            setTextFieldInput("");
            setTagsValue([...new Set(v.map((x) => x.toLowerCase()))]);
          }}
          name="tags"
        >
          <div className="relative z-10">
            <Combobox.Input
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Backspace" && textFieldInput === "") {
                  // Remove the last tag
                  setTagsValue((tags) => tags.slice(0, tags.length - 1));
                }
              }}
              onChange={(event) => setTextFieldInput(event.target.value)}
              onFocus={() => textFieldInput != "" && setTextFieldInput("")}
              ref={inputRef}
              className="h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t(msg`Enter tag name`)}
            />
            <div className="absolute mt-1 w-full rounded-md bg-background shadow-lg">
              <Combobox.Options className="shadow-xs max-h-60 overflow-auto rounded-md py-1 leading-6 focus:outline-hidden sm:text-sm sm:leading-5">
                {[...filteredTags.values()]
                  .filter((x) => x?.trim() !== "")
                  .map((tag) => (
                    <Combobox.Option
                      key={tag}
                      value={tag}
                      className={({ active }) => {
                        return classNames(
                          "relative cursor-default select-none py-2 pl-3 pr-9 focus:outline-hidden",
                          active ? "bg-muted text-foreground" : "text-muted-foreground",
                        );
                      }}
                    >
                      {({ active, selected }) => (
                        <>
                          <span className={classNames("block truncate", selected ? "font-semibold" : "font-normal")}>
                            {tag}
                          </span>
                          {selected && (
                            <span
                              className={classNames("absolute inset-y-0 right-0 flex items-center pr-4 text-green-600")}
                            >
                              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
              </Combobox.Options>
            </div>
          </div>
        </Combobox>
        <div className="mt-6">
          <div><Trans>Selected Tags ({article.tags.length})</Trans></div>
          {article.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap">
              {article.tags?.map((tag, i) => (
                <Badge variant="secondary" className="mr-1" key={i}>
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setTagsValue((existing) => existing.filter((p) => p !== tag));
                    }}
                  >
                    <XMarkIcon className="h-3 w-3 ml-1" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        <hr className="mt-6" />
        <div className="mt-6">
          <div className="mb-3"><Trans>Available Tags ({unusedUserTags.length})</Trans></div>
          {unusedUserTags.length > 0 && (
            <div className="mb-6 flex flex-wrap">
              {unusedUserTags.map((tag, i) => (
                <Badge variant="default" className="mr-1" key={i}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setTagsValue((existing) => [...existing, tag]);
                    }}
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                  </button>
                  <span>{tag}</span>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// problem - when we add a new tag that's already selected, it removes it
